import {useMinSpin} from "../../hooks/useMinSpin";
import {useState} from "react";
import axios from "axios";
import {PATHS_API} from "../../../enum";
import ProductImage from "../../shared/ProductImage/ProductImage";
import ButtonLabel from "../../shared/ButtonLoader/ButtonLoader";
import {guessColor, IAdminProduct} from "./adminTypes";

interface IPart { productId: string, name: string, color: string }

// Об'єднання вибраних товарів в один із кольорами. Ті самі перевірки (ціна, тип, особисті) робить і сервер.
const MergePanel = ({ selected, onCancel, onMerged }: {
    selected: IAdminProduct[],
    onCancel: () => void,
    onMerged: (message: string) => void,
}) => {
    const [baseId, setBaseId] = useState(selected[0]._id);
    const [parts, setParts] = useState<IPart[]>(() => selected.map((p) => ({ productId: p._id, ...guessColor(p) })));
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    // спінер видно щонайменше пів секунди (useMinSpin)
    const saveSpin = useMinSpin(saving);

    const byId = new Map(selected.map((p) => [p._id, p]));
    const base = byId.get(baseId)!;

    const problems = selected.flatMap((p) => [
        p.personal ? `«${p.name}» — особистий товар` : "",
        p.variants?.length ? `«${p.name}» уже має кольори` : "",
        p.type !== base.type ? `«${p.name}» має інший тип, ніж основний` : "",
        p.price !== base.price ? `«${p.name}» коштує ${p.price} ₴, а основний — ${base.price} ₴` : "",
    ]).filter(Boolean);

    const setPart = (i: number, patch: Partial<IPart>) => setParts((ps) => ps.map((p, j) => j === i ? { ...p, ...patch } : p));
    const move = (i: number, dir: -1 | 1) => setParts((ps) => {
        const next = [...ps];
        const j = i + dir;
        if (j < 0 || j >= next.length) return ps;
        [next[i], next[j]] = [next[j], next[i]];
        return next;
    });

    const merge = async () => {
        if (problems.length || saving) return;
        if (parts.some((p) => !p.name.trim())) { setError("Вкажіть назву кожного кольору"); return; }

        setSaving(true);
        setError("");
        try {
            await axios.post(PATHS_API.ADMIN_MERGE, { baseId, parts: parts.map((p) => ({ ...p, name: p.name.trim() })) }, { withCredentials: true });
            onMerged(`Об'єднано в «${base.name}» з ${parts.length} кольорами`);
        } catch (err: any) {
            const message = err.response?.data?.message;
            setError(Array.isArray(message) ? message.join("; ") : message || "Не вдалося об'єднати");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="adm-overlay" onClick={onCancel}>
            <div className="adm-editor adm-merge" onClick={(e) => e.stopPropagation()}>
                <div className="adm-editor-head">
                    <h4>Об'єднати в один товар із кольорами</h4>
                    <button type="button" className="adm-icon-btn" onClick={onCancel} aria-label="Закрити">✕</button>
                </div>

                <div className="adm-editor-body">
                    <p className="adm-muted">
                        Лишиться <b>основний</b> товар (його назва, опис, ціна, категорії й сервери), решта стануть його кольорами
                        й зникнуть із магазину. Копії зберігаються в архіві. Порядок рядків — порядок кольорів, перший показується за замовчуванням.
                    </p>

                    { parts.map((part, i) => {
                        const p = byId.get(part.productId)!;
                        return (
                            <div key={part.productId} className={`adm-merge-row ${part.productId === baseId ? "is-base" : ""}`}>
                                <ProductImage className="adm-variant-img" src={p.imageUrl} alt="" />
                                <div className="adm-merge-info">
                                    <b>{p.name}</b>
                                    <span className="adm-muted adm-mono">{p.className || "—"} · {p.price} ₴</span>
                                </div>
                                <input type="color" value={part.color} onChange={(e) => setPart(i, { color: e.target.value })} aria-label="Колір" />
                                <input className="adm-merge-name" value={part.name} onChange={(e) => setPart(i, { name: e.target.value })} placeholder="Назва кольору" maxLength={40} />
                                <label className="adm-base-radio">
                                    <input type="radio" name="base" checked={part.productId === baseId} onChange={() => setBaseId(part.productId)} />
                                    основний
                                </label>
                                <div className="adm-variant-actions">
                                    <button type="button" className="adm-icon-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Вище">↑</button>
                                    <button type="button" className="adm-icon-btn" onClick={() => move(i, 1)} disabled={i === parts.length - 1} aria-label="Нижче">↓</button>
                                </div>
                            </div>
                        );
                    })}

                    { problems.length > 0 && <ul className="adm-errors">{ problems.map((m, i) => <li key={i}>{m}</li>) }</ul> }
                    { error && <ul className="adm-errors"><li>{error}</li></ul> }
                </div>

                <div className="adm-editor-foot">
                    <button type="button" className="adm-ghost-btn" onClick={onCancel}>Скасувати</button>
                    <button type="button" className={`adm-primary-btn ${saveSpin ? "btn-loading" : ""}`} onClick={merge} disabled={saveSpin || problems.length > 0}>
                        <ButtonLabel loading={saveSpin} loadingText="Об'єднуємо...">Об'єднати {parts.length} товари</ButtonLabel>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MergePanel;
